import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import NotFound from '../../common/404';
import { Person, Unit, OntotisDropDown, PositionDropDown } from '../../../utils/types';
import { getPersonsSingle, updatePerson, deletePerson } from '../../../utils/apicall/personsApi';
import { getOntotisDropDown, getPositionsDropDown, getUnitsDropDown } from '../../../utils/apicall/commonApi';
import { CONFIG } from '../../../utils/config';
import SectionPersonAssignments from './SectionPersonAssignments';
import { handleError, ApiError } from '../../../utils/error/error';
import { processError } from '../../../utils/error/errorUtils';
import GeneralErrorPage from '../../common/general-error';
import ErrorMessage from '../../../components/errorMessage';
import DeleteConfirmationModal from '../../../components/DeleteConfirmationModal';
import { authService } from '../../../utils/authServices';

const PersonEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [isValidId, setIsValidId] = useState(true);
  const [data, setData] = useState<Person | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  const [ontotisDropDown, setOntotisDropDown] = useState<OntotisDropDown[]>([]);
  const [positionDropDown, setPositionDropDown] = useState<PositionDropDown[]>([]);
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string[] }>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const [formData, setFormData] = useState({
    id: null,
    firstName: '',
    lastName: '',
    phone1: '',
    phone2: '',
    email1: '',
    email2: '',
    address: '',
    city: '',
    postalCode: '',
    ontoti_id: '',
    position_id: '',
  });

  useEffect(() => {
    if (!id || isNaN(Number(id))) {
      setIsValidId(false);
      setIsLoading(false);
    }
  }, [id]);

  const loadData = useCallback(async () => {
    if (isValidId) {
      setIsLoading(true);
      try {
        const response = await getPersonsSingle(parseInt(id!, 10));
        if (response) {
          setData(response);
          setFormData(response);
        }
      } catch (error) {
        handleError(error);
        processError(error, setErrors, setErrorMessage, setSuccessMessage);
        if (error instanceof ApiError && error.statusCode === 404) {
          setIsValidId(false);
        }
      } finally {
        setIsLoading(false);
      }
    }
  }, [id, isValidId]);

  const loadUnits = useCallback(async () => {
    try {
      const response = await getUnitsDropDown();
      if (response) setUnits(response);
    } catch (error) {
      console.error('An error occurred while fetching units.', error);
    }
  }, []);

  const loadOntotisDropDown = useCallback(async () => {
    try {
      const response = await getOntotisDropDown();
      if (response) setOntotisDropDown(response);
    } catch (error) {
      console.error('An error occurred while fetching ontotis dropdown.', error);
    }
  }, []);

  const loadPositionDropDown = useCallback(async () => {
    try {
      const response = await getPositionsDropDown();
      if (response) setPositionDropDown(response);
    } catch (error) {
      console.error('An error occurred while fetching positions dropdown.', error);
    }
  }, []);

  useEffect(() => {
    loadUnits();
    loadData();
    loadPositionDropDown();
    loadOntotisDropDown();
    authService.refreshAuthToken();
  }, [loadUnits, loadData, loadPositionDropDown, loadOntotisDropDown]);

  if (!isValidId) {
    return <NotFound />;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true); // Set loading to true while saving
    try {
      const response = await updatePerson(parseInt(id!, 10), formData);
      setSuccessMessage('Αποθήκευση επιτυχής');
      const returnTo = location.state?.returnTo || "-1";
      setTimeout(() => {
        // Go back to the previous page and pass a `refresh` flag
      //   navigate(returnTo, { state: { refresh: true } });
    //   navigate(returnTo, { state: { refresh: true } });
      }, 800); // Adjust delay if necessary*/
     
    } catch (error) {
      handleError(error);
      processError(error, setErrors, setErrorMessage, setSuccessMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setShowDeleteModal(false);
    setIsLoading(true); // Set loading to true while deleting
    try {
      await deletePerson(parseInt(id!, 10));
      setSuccessMessage('To πρόσωπο διαγράφηκε με επιτυχία!');
 
      const returnTo = location.state?.returnTo || "-1";
      //const response = await updatePerson(parseInt(id!, 10), formData); // API call to update person
       
      
       setTimeout(() => {
        // Go back to the previous page and pass a `refresh` flag
        navigate(returnTo, { state: { refresh: true } });
      }, 800); // Adjust delay if necessary*/
    } catch (error) {
      handleError(error);
      processError(error, setErrors, setErrorMessage, setSuccessMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

 
  return (
    <div className="content">
      <section className="ontotita py-5">
        <div className="container mt-4">
          <div className="card p-5">
            <h2 className="h5 mb-2 text-primary">Επεξεργασία Προσώπου</h2>
            <hr />
            <form onSubmit={handleSubmit}>
            {successMessage && (
                            <div className="alert alert-success alert-dismissible fade show" role="alert">
                                <strong></strong> {successMessage}
                                <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="alert"
                                aria-label="Close"
                                ></button>
                            </div>
                            )}
                            {errorMessage && (
                            <div className="alert alert-danger alert-dismissible fade show" role="alert">
                                <strong></strong> {errorMessage}
                                <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="alert"
                                aria-label="Close"
                                ></button>
                            </div>
                            )}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="firstName" className="form-label">Όνομα</label>
                  <div className="input-group">
                    <span className="input-group-text"><i className="bi bi-person"></i></span>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      className="form-control"
                      required
                      value={formData?.firstName || ''}
                      onChange={handleChange}
                    />
                  </div>
                  {errors && errors.hasOwnProperty('firstName') && (<ErrorMessage message={errors.firstName[0]} />)}
                </div>
                <div className="col-md-6">
                  <label htmlFor="lastName" className="form-label">Επώνυμο</label>
                  <div className="input-group">
                    <span className="input-group-text"><i className="bi bi-person"></i></span>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      className="form-control"
                      required
                      value={formData?.lastName || ''}
                      onChange={handleChange}
                    />
                  </div>
                  {errors && errors.hasOwnProperty('lastName') && (<ErrorMessage message={errors.lastName[0]} />)}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="phone1" className="form-label">Τηλέφωνο 1</label>
                  <div className="input-group">
                    <span className="input-group-text"><i className="bi bi-telephone"></i></span>
                    <input
                      type="text"
                      id="phone1"
                      name="phone1"
                      className="form-control"
                      required
                      value={formData?.phone1 || ''}
                      onChange={handleChange}
                    />
                  </div>
               
                </div>
                {errors && errors.hasOwnProperty('phone1') && (<ErrorMessage message={errors.phone1[0]} />)}
                <div className="col-md-6">
                  <label htmlFor="phone2" className="form-label">Τηλέφωνο 2</label>
                  <div className="input-group">
                    <span className="input-group-text"><i className="bi bi-telephone"></i></span>
                    <input
                      type="text"
                      id="phone2"
                      name="phone2"
                      className="form-control"
                      value={formData?.phone2 || ''}
                      onChange={handleChange}
                    />
                  </div>
                 
                </div>
                {errors && errors.hasOwnProperty('phone2') && (<ErrorMessage message={errors.phone2[0]} />)}
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="email1" className="form-label">Email 1</label>
                  <div className="input-group">
                    <span className="input-group-text"><i className="bi bi-envelope"></i></span>
                    <input
                      type="email"
                      id="email1"
                      name="email1"
                      className="form-control"
                      required
                      value={formData?.email1 || ''}
                      onChange={handleChange}
                    />
                  </div>
                  {errors && errors.hasOwnProperty('email1') && (<ErrorMessage message={errors.email1[0]} />)}
                </div>
                <div className="col-md-6">
                  <label htmlFor="email2" className="form-label">Email 2</label>
                  <div className="input-group">
                    <span className="input-group-text"><i className="bi bi-envelope"></i></span>
                    <input
                      type="email"
                      id="email2"
                      name="email2"
                      className="form-control"
                      value={formData?.email2 || ''}
                      onChange={handleChange}
                    />
                  </div>
                  {errors && errors.hasOwnProperty('email2') && (<ErrorMessage message={errors.email2[0]} />)}
                </div>
               
              </div>

              <div className="row mb-3">
                <div className="col-md-4">
                  <label htmlFor="address" className="form-label">Διεύθυνση</label>
                  <div className="input-group">
                    <span className="input-group-text"><i className="bi bi-house-door"></i></span>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      className="form-control"
                      value={formData?.address || ''}
                      onChange={handleChange}
                    />
                  </div>
                  {errors && errors.hasOwnProperty('address') && (<ErrorMessage message={errors.address[0]} />)}
                </div>
                <div className="col-md-4">
                  <label htmlFor="city" className="form-label">Πόλη</label>
                  <div className="input-group">
                    <span className="input-group-text"><i className="bi bi-geo-alt"></i></span>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      className="form-control"
                      value={formData?.city || ''}
                      onChange={handleChange}
                    />
                  </div>
                  {errors && errors.hasOwnProperty('address') && (<ErrorMessage message={errors.address[0]} />)}
                </div>
                <div className="col-md-4">
                  <label htmlFor="postalCode" className="form-label">ΤΚ</label>
                  <div className="input-group">
                    <span className="input-group-text"><i className="bi bi-mailbox"></i></span>
                    <input
                      type="text"
                      id="postalCode"
                      name="postalCode"
                      className="form-control"
                      value={formData?.postalCode || ''}
                      onChange={handleChange}
                    />
                  </div>
                  
                </div>
                {errors && errors.hasOwnProperty('postalCode') && (<ErrorMessage message={errors.postalCode[0]} />)}
              </div>

              <div className="text-end mt-4">
                <button type="submit" className="btn btn-primary">  <i className="bi bi-save me-1"></i> Αποθήκευση</button>
                             <button type="button" className="btn btn-danger ms-2"   onClick={() => setShowDeleteModal(true)}>
                                <i className="bi bi-trash me-1"></i> Διαγραφή
                            </button>
                            {/* Delete Confirmation Modal */}
                            <DeleteConfirmationModal
                                show={showDeleteModal}
                                onConfirm={handleDelete}
                                onCancel={() => setShowDeleteModal(false)} // Just close the modal
                                title="Επιβεβαίωση Διαγραφής"
                                message="Αυτή η ενέργεια θα διαγράψει οριστικά το επιλεγμένο πρόσωπο. Είστε βέβαιοι;"
                                confirmText="Ναι, Διαγραφή"
                                cancelText="Ακύρωση"
                            />
              </div>
            </form>
          </div>
        </div>
      </section>

      {data && (
        <SectionPersonAssignments 
          personData={data} 
          ontotisDropDown={ontotisDropDown} 
          positionDropDown={positionDropDown} 
          onRefresh={loadData} // Pass loadData as the onRefresh callback
        />
      )}
    </div>
  );
};

export default PersonEdit;
